import { motion } from "framer-motion";
import {
  CreditCard,
  Download,
  KeyRound,
  SlidersHorizontal,
  UserPlus,
} from "lucide-react";
import { VARIANT } from "@/constants/links";

const isVip = VARIANT === "vip";

const Star = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" />
  </svg>
);

// Variante VIP: l'attivazione NON passa da Google Play. Sequenza reale del
// prodotto: account dedicato -> pagamento Stripe 59,99 EUR/mese -> APK Android.
// (La variante standard mantiene i passi "famiglia/codice/regole" gia' in uso.)
const STEPS_VIP = [
  {
    n: "01",
    icon: UserPlus,
    iconBg: "bg-violet text-white shadow-lg shadow-violet/40",
    title: "Crea l'account VIP",
    desc: "Registrati sul sito VIP. L'account e la dashboard sono separati da quelli della versione Standard distribuita su Google Play.",
    bg: "bg-softyellow",
    border: "border-yellow",
    pill: "bg-yellow text-ink",
  },
  {
    n: "02",
    icon: CreditCard,
    iconBg: "bg-pinky-deep text-white shadow-lg shadow-pinky-deep/40",
    title: "Abbonati con Stripe",
    desc: "59,99 € al mese con pagamento sicuro Stripe. Non è un acquisto in-app su Google Play: gestione e rinnovo restano fuori dallo store.",
    bg: "bg-softpurple",
    border: "border-violet/70",
    pill: "bg-violet text-white",
  },
  {
    n: "03",
    icon: Download,
    iconBg: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40",
    title: "Installa l'APK e associa",
    desc: "Scarichi l'APK VIP, lo installi sul dispositivo Android e lo associ con il codice generato dal tuo account: da quel momento monitori tutto dal pannello.",
    bg: "bg-pinky",
    border: "border-pinky-deep/60",
    pill: "bg-pinky-deep text-white",
  },
];

const STEPS = [
  {
    n: "01",
    icon: UserPlus,
    iconBg: "bg-violet text-white shadow-lg shadow-violet/40",
    title: "Crea la tua famiglia",
    desc: "Aggiungi i profili dei tuoi figli dal pannello Genitore e crea il tuo nucleo protetto, in pochi secondi.",
    bg: "bg-softyellow",
    border: "border-yellow",
    pill: "bg-yellow text-ink",
  },
  {
    n: "02",
    icon: KeyRound,
    iconBg: "bg-pinky-deep text-white shadow-lg shadow-pinky-deep/40",
    title: "Associa con il codice",
    desc: "Collega ogni dispositivo con un codice a 4 cifre monouso e a scadenza: una connessione sicura, senza passaggi complicati.",
    bg: "bg-softpurple",
    border: "border-violet/70",
    pill: "bg-violet text-white",
  },
  {
    n: "03",
    icon: SlidersHorizontal,
    iconBg: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40",
    title: "Scegliete le regole",
    desc: "Imposta insieme limiti, fasce orarie e luoghi importanti. Regole chiare sono regole che si rispettano.",
    bg: "bg-pinky",
    border: "border-pinky-deep/60",
    pill: "bg-pinky-deep text-white",
  },
];

export const HowItWorks = () => (
  <section id="come-funziona" className="relative scroll-mt-24 overflow-hidden bg-white py-24 md:py-32">
    <div
      aria-hidden="true"
      className="absolute -left-20 top-20 h-52 w-52 rounded-full bg-pinky/70"
    />
    <div
      aria-hidden="true"
      className="absolute -right-24 bottom-16 h-64 w-64 rounded-full border-[16px] border-yellow"
    />
    <div aria-hidden="true" className="absolute left-[5%] top-16 h-32 w-32 bg-dots-pink rounded-3xl" />
    <Star className="animate-float-slow absolute right-[10%] top-24 hidden h-8 w-8 text-violet lg:block" />

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mx-auto mb-16 max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-violet px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-violet/30">
          Come funziona
        </span>
        <h2 className="isolate mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
          {isVip ? (
            <>
              Tre passi verso la{" "}
              <span className="text-highlight">supervisione completa</span>
            </>
          ) : (
            <>
              Tre passi verso la{" "}
              <span className="text-highlight">serenità digitale</span>
            </>
          )}
        </h2>
        <p className="mt-4 text-base font-medium leading-relaxed text-ink/70 sm:text-lg">
          {isVip
            ? "VIP non passa da Google Play: crei l'account dedicato, paghi con Stripe e installi l'APK sul dispositivo Android da supervisionare."
            : "Un percorso semplice, pensato per i genitori: in pochi minuti la famiglia è protetta."}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {(isVip ? STEPS_VIP : STEPS).map((step, i) => (
          <motion.article
            key={step.n}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`howitworks-step-${i + 1}`}
            className={`group relative h-full overflow-hidden rounded-3xl border-[3px] ${step.border} ${step.bg} p-8 card-pop transition-all duration-300 hover:-translate-y-1.5`}
          >
            {/* Il numerone decorativo "01/02/03" era ridondante col badge
                "Passo 01/02/03": rimosso nella variante VIP (2026-10-10, su
                segnalazione). Nella variante standard resta invariato. */}
            {!isVip && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-5 right-2 font-display text-9xl font-black text-white"
              >
                {step.n}
              </span>
            )}
            <span
              className={`relative inline-flex rounded-full ${step.pill} px-4 py-1.5 font-display text-xs font-black uppercase tracking-widest shadow-md`}
            >
              Passo {step.n}
            </span>
            <span
              className={`relative mt-7 flex h-16 w-16 items-center justify-center rounded-2xl ${step.iconBg} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}
            >
              <step.icon size={30} />
            </span>
            <h3 className="relative mt-5 font-display text-2xl font-black tracking-tight text-ink sm:text-[1.7rem]">
              {step.title}
            </h3>
            <p className="relative mt-3 text-[15px] font-medium leading-relaxed text-ink/75">
              {step.desc}
            </p>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
